/**
 * Whether we have already asked this phone for background location.
 *
 * Exists because iOS reports "Allow Once" and "While Using" identically
 * (`authorizedWhenInUse`) and `canAskAgain` reads TRUE exactly when we need
 * it false (see background-permission-route.ts) — the app must remember for
 * itself. Zero imports on purpose: this is the one input to the tested pure
 * routing decision that cannot be read from the OS, so its failure behaviour
 * must be drivable by a test (inlined in stay-visible.ts, the first version
 * shipped broken behind green tests).
 *
 * TWO LAYERS, because persistence fails silently in both directions
 * (`storage.set` rejects on keychain errors; `storage.get` catches and
 * returns null — indistinguishable from "never asked"). Memory stops repeat
 * silent prompts within a process; the persisted write covers the next
 * process. Guarantee, stated precisely: with a broken keychain the cost is
 * AT MOST ONE silent attempt per process — bounded degradation, not
 * process-safe persistence. A failed write never blocks the request itself.
 *
 * NEITHER OPERATION MAY HANG (`PERSISTENCE_BUDGET_MS`): `record` is awaited
 * BEFORE the OS prompt (a hung write = prompt never shown — the exact
 * failure this module prevents, by a different road) and `wasAttempted`
 * gates the sheet's button (a hung read = "Requesting…" forever). The
 * deadline is a deliberate 8-line duplicate of with-timeout.ts: the test
 * runner resolves no `@/` alias, so importing it would introduce a new
 * module-resolution pattern just for the harness. Both copies are test-driven,
 * which is what keeps drift visible.
 */

/** Where the attempt is persisted. */
export const BACKGROUND_ATTEMPT_KEY = "opncue.stayVisible.bgAsked";

const RECORDED = "1";

/**
 * How long either keychain operation may take before we give up on it.
 *
 * These are local native calls that normally complete in single-digit
 * milliseconds; the budget is here to bound a wedged bridge, not to race a slow
 * device, so it is generous by two orders of magnitude and still bounded. Both
 * paths behind it degrade to "not attempted", which is the answer that keeps
 * the feature usable rather than the one that keeps the bookkeeping tidy.
 */
export const PERSISTENCE_BUDGET_MS = 2000;

/**
 * The outcome of one bounded keychain operation.
 *
 * Tagged because `null` is a legitimate VALUE here — an absent key — as well as
 * the obvious way to signal failure. See `bounded` for what conflating them
 * cost.
 */
type Attempt<T> = { ok: true; value: T } | { ok: false };

const FAILED = { ok: false } as const;

/** The slice of `storage` this needs. Injected so a test can make it fail. */
export type AttemptStore = {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
};

export type BackgroundAttemptMemory = {
  /** Have we ever put the OS prompt in front of this user? */
  wasAttempted(): Promise<boolean>;
  /** Record that we are about to. Never rejects. */
  record(): Promise<void>;
};

export function createBackgroundAttemptMemory(
  store: AttemptStore,
  budgetMs: number = PERSISTENCE_BUDGET_MS,
): BackgroundAttemptMemory {
  let askedThisSession = false;
  let warned = false;

  /**
   * Run one keychain operation under the budget.
   *
   * Returns a TAGGED result rather than `T | null`, and that is not stylistic.
   * `store.get` returns `null` for a key that is simply not there — the correct,
   * expected answer for anybody who has never been asked — and an untagged
   * version used `null` for failure too. The two collapsed, so every first-run
   * user was logged as a keychain failure. The log line even claimed the
   * opposite: it said the operation name was "all a reader needs to tell a
   * wedged keychain apart from a user who genuinely has not been asked", while
   * being emitted for precisely that user. An alert that fires on the happy
   * path is worse than no alert, because it trains the reader to ignore the
   * real one.
   */
  async function bounded<T>(work: () => Promise<T>, what: string): Promise<Attempt<T>> {
    const settled = await new Promise<Attempt<T>>((resolve) => {
      const timer = setTimeout(() => resolve(FAILED), budgetMs);
      const settle = (result: Attempt<T>) => {
        clearTimeout(timer);
        resolve(result);
      };
      try {
        // A store that throws SYNCHRONOUSLY never produces a promise to bound,
        // so it is caught here rather than assumed away.
        //
        // The rejection handler is attached whether or not the timer has
        // already fired, so work that fails AFTER timing out settles into a
        // no-op rather than an unhandled rejection.
        void work().then(
          (value) => settle({ ok: true, value }),
          () => settle(FAILED),
        );
      } catch {
        settle(FAILED);
      }
    });

    if (!settled.ok) warnOnce(what);
    return settled;
  }

  /**
   * Say it once, and say nothing about the person.
   *
   * Once, because a wedged keychain fails on EVERY preview and every request —
   * the sheet re-reads each time it opens — so an undeduplicated line turns one
   * fact ("persistence is down, the feature is degraded") into a scrolling wall
   * that buries whatever is logged next to it.
   *
   * Deduplicated per memory rather than per module. In production
   * `createBackgroundAttemptMemory` is called once, at module scope in
   * `stay-visible.ts`, so per-memory IS per-process there — while module-level
   * state would make the tests below order-dependent, with the first failing
   * test silencing every later one's assertion.
   *
   * Nothing here identifies anybody: the key is a compile-time constant and the
   * value is the literal "1". Matches the shape `storage.ts` already uses for
   * its own read failures.
   */
  function warnOnce(what: string): void {
    if (warned) return;
    warned = true;
    console.warn(
      `[background-attempt] keychain ${what} failed or timed out; ` +
        `falling back to session memory for this process`,
    );
  }

  return {
    async wasAttempted(): Promise<boolean> {
      // Session memory first, and it is not merely an optimisation: a keychain
      // read that fails returns `null`, which reads exactly like "never asked".
      // Consulting it first means neither a failed read nor a slow one can
      // erase an attempt we watched ourselves make.
      if (askedThisSession) return true;
      const read = await bounded(() => store.get(BACKGROUND_ATTEMPT_KEY), "read");
      // A read we could not make degrades to "not attempted" — the answer that
      // keeps the feature usable. A read that succeeded and found nothing gives
      // the same answer, but is not a failure and is not logged as one.
      return read.ok && read.value === RECORDED;
    },

    async record(): Promise<void> {
      // Set BEFORE the write is started, not after. The write can reject, and
      // it can also simply never settle — either would otherwise leave the
      // attempt unrecorded for the rest of the session while the OS prompt was
      // already on screen.
      askedThisSession = true;
      await bounded(() => store.set(BACKGROUND_ATTEMPT_KEY, RECORDED), "write");
    },
  };
}
